import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';

import PageTitle from '../components/generic/PageTitle';
import QuickActionCard from '../components/generic/QuickActions';
import RequirementForm from '../components/requirements/RequirementForm';
import AiAssistantCard from 'components/requirements/AiAssistantCard';
import Breadcrumbs from 'components/generic/Breadcrumbs';
import { toast } from 'react-hot-toast';
import { Sparkles, CheckCircle2, AlertTriangle, X } from "lucide-react";

const RequirementsPage = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const isDraftRef = useRef(false);
  const navigate = useNavigate();

  const { createProject, updateProject, getProject, loading, error } = useProject();

  const [submitError, setSubmitError] = useState(null);
  const [initialData, setInitialData] = useState(null);

  const showSuccessToast = (title, message) => {
    toast.custom(
      (t) => (
        <div className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-md w-full bg-[#F0FDF4] border border-[#22C55E] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}>
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button onClick={() => toast.dismiss(t.id)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  const showErrorToast = (title, message) => {
    toast.custom(
      (t) => (
        <div className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}>
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button onClick={() => toast.dismiss(t.id)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  useEffect(() => {
    if (id) {
      const fetchProjectData = async () => {
        const response = await getProject(id);
        if (response.success) {
          setInitialData(response.data.project || response.data);
        } else {
          showErrorToast("Error", "Could not load project details");
        }
      };
      fetchProjectData();
    }
  }, [id]);

  const handleSubmit = async (data) => {
    console.log("=== FORM SUBMISSION START ===");
    setSubmitError(null);

    if (!data.project_name) {
      setSubmitError("Project Name is required");
      return;
    }

    const user = JSON.parse(localStorage.getItem('user')) || {};
    const organizationType = user?.organizationType;

    const projectData = {
      project_name: data.project_name,
      description: data.description,
      event_type: data.event_type,
      event_date: data.event_date instanceof Date ? data.event_date.toISOString().split('T')[0] : data.event_date,
      duration_hours: parseFloat(data.duration_hours) || 0,
      budget: parseFloat(data.budget) || 0,
      expected_viewers: parseInt(data.expected_viewers) || 0,
      stream_quality: data.stream_quality,
      crew_size_needed: parseInt(data.crew_size_needed) || 0,
      event_location: data.event_location || '',
      streaming_platforms: data.streaming_platforms || [],
      streaming_destinations: data.streaming_destinations || [],
      crew_roles: data.crew_roles || [],
      required_skills: data.required_skills || [],
      equipments_needed: data.equipment_categories_needed || [],
      start_time: data.start_time || '',
      end_time: data.end_time || '',
      is_draft: isDraftRef.current ? 1 : 0
    };

    if (organizationType === 2) {
      projectData.organization_type = organizationType;
    }

    try {
      let result;
      if (id) {
        console.log("Calling updateProject API...");
        result = await updateProject(id, projectData);
      } else {
        console.log("Calling createProject API...");
        result = await createProject(projectData);
      }

      console.log("API response received:", result);

      const isSuccessful = result.success || (result.data && result.data.success) || result.status === 200 || result.status === 201;

      if (isSuccessful) {
        const wasDraftAction = isDraftRef.current;
        isDraftRef.current = false;

        if (wasDraftAction) {
          showSuccessToast("Draft Saved", "Your project has been saved to drafts.");
          setTimeout(() => {
            navigate('/book-a-shoot', { state: { activeTab: "Draft" } });
          }, 500);
        } else {
          showSuccessToast("Success!", id ? "Project updated successfully." : "Your project has been created.");
          console.log("Navigating to AI matchmaker...");
          setTimeout(() => {
            navigate("/requirements/ai-matchmaker");
          }, 100);
        }
      } else {
        console.error("Submission failed:", result.error);
        setSubmitError(result.error || 'Failed to save project');
      }
    } catch (err) {
      console.error('Project submission error:', err);
      setSubmitError('An unexpected error occurred');
    }
    console.log("=== FORM SUBMISSION END ===");
  };

  const handleExternalSubmit = () => {
    isDraftRef.current = false;
    formRef.current?.requestSubmit();
  };

  const handleSaveDraft = () => {
    isDraftRef.current = true;
    formRef.current?.requestSubmit();
  };

  return (
    <div className="requirements-content p-6">
      <Breadcrumbs />
      <PageTitle
        title={id ? "Edit Booking Requirements" : "Booking Requirements"}
        description={id ? "Update your draft and move forward." : "Create a new project booking and define your requirements."}
      />

      {(submitError || error) && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {submitError || error}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 md:self-stretch ">
        <div className="md:w-3/4">
          <RequirementForm
            passRef={formRef}
            handleSubmit={handleSubmit}
            initialData={initialData}
          />
        </div>

        <div className="md:w-1/4">
          <div className="sticky top-6 flex flex-col justify-start items-start gap-6">
            <QuickActionCard
              handleClick={handleExternalSubmit}
              buttonText={loading ? "Saving..." : (id ? "Update & Find Matches" : "Submit & Find Matches")}
              icon={<Sparkles className="w-4 h-4" />}
              disabled={loading}
              onDraftClick={handleSaveDraft}
            />
            <AiAssistantCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequirementsPage;
